import { isNonNullish, unique } from '@epicuro-next/utilities';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import {
  Basket,
  BasketRowsGroupType,
} from 'graphql-api/baskets-api-interfaces';

import {
  collapseBasketLines,
  deselectBasketLine,
  expandBasketLines,
  groupDtoBasketSuccess,
  resetBasketItemsSelection,
  resetExpandedLines,
  selectBasketLine,
} from '../actions/basket-ui.actions';

export const basketUiFeatureKey = 'basket-ui';

function selectId(entity: BasketsByGroupingsEntity) {
  return entity.basketId;
}

export type BasketsByGroupings = {
  [key in BasketRowsGroupType]?: Basket;
};

export type BasketsByGroupingsEntity = BasketsByGroupings & {
  basketId: string;
};

export interface State extends EntityState<BasketsByGroupingsEntity> {
  selectedBasketItemIds: string[];
  expandedBasketLineIds: string[];
}

export const adapter = createEntityAdapter({
  selectId,
});

export const initialState: State = adapter.getInitialState({
  selectedBasketItemIds: [],
  expandedBasketLineIds: [],
});

export const reducer = createReducer(
  initialState,
  on(selectBasketLine, (state, { basketItemIds }): State => {
    return {
      ...state,
      selectedBasketItemIds: [
        ...state.selectedBasketItemIds,
        ...basketItemIds,
      ].filter(unique),
    };
  }),
  on(deselectBasketLine, (state, { basketItemIds }): State => {
    return {
      ...state,
      selectedBasketItemIds: basketItemIds.reduce(
        (res, id) => res.filter((selectedId) => selectedId !== id),
        state.selectedBasketItemIds,
      ),
    };
  }),
  on(resetBasketItemsSelection, (state): State => {
    return {
      ...state,
      selectedBasketItemIds: [],
    };
  }),
  on(expandBasketLines, (state, { ids }): State => {
    return {
      ...state,
      expandedBasketLineIds: [...state.expandedBasketLineIds, ...ids].filter(
        unique,
      ),
    };
  }),
  on(collapseBasketLines, (state, { ids }): State => {
    return {
      ...state,
      expandedBasketLineIds: state.expandedBasketLineIds.filter(
        (expanded) => !ids.includes(expanded),
      ),
    };
  }),
  on(resetExpandedLines, (state): State => {
    return {
      ...state,
      expandedBasketLineIds: [],
    };
  }),
  on(groupDtoBasketSuccess, (state, { groupedBaskets }) => {
    const basketId = getIdFromGroupedCollection(groupedBaskets);

    return basketId
      ? adapter.setOne({ ...groupedBaskets, basketId }, state)
      : state;
  }),
);

function getIdFromGroupedCollection(groupedBaskets: BasketsByGroupings) {
  return Object.values(groupedBaskets).find((basket) =>
    isNonNullish(basket.basketId),
  )?.basketId;
}
